import { CommonModule, ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged } from 'rxjs';

import { IConfig, IDynamicElement, Paging } from '../_shared/models';
import {
    IGoogleSearchItem,
    IGoogleSearchQueries,
    IGoogleSearchQuery,
    IGoogleSearchResult,
    GoogleSearchService,
} from '../_shared/services/google-search.service';
import { GridComponent } from '../grid/grid.component';
import { HeadingComponent } from '../heading/heading.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { SearchComponent } from '../search/search.component';
import { SpacerComponent } from '../spacer/spacer.component';

@Component({
    selector: 'sirius-search-page-component',
    templateUrl: './search-page.component.html',
    styleUrls: ['./search-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpacerComponent,
        GridComponent,
        HeadingComponent,
        SearchComponent,
        PaginationComponent,
        CommonModule,
        TranslateModule,
    ]
})
export class SearchPageComponent implements OnInit, IDynamicElement {
    private _searchService = inject(GoogleSearchService);
    private _viewportScroller = inject(ViewportScroller);
    private _route = inject(ActivatedRoute);
    private _router = inject(Router);

    config?: IConfig;
    paging = new Paging();
    searchQuery = '';
    items: IGoogleSearchItem[] = [];
    queries?: IGoogleSearchQueries;
    fetching$!: Observable<boolean>;
    query?: string;

    private _paramSub?: Subscription;

    private _hasLoaded = new BehaviorSubject<boolean>(false);
    hasLoaded$ = this._hasLoaded.asObservable();

    get request(): IGoogleSearchQuery | undefined {
        return this.queries?.request[0];
    }

    get numberOfResults(): string {
        return this.request?.totalResults ?? '0';
    }

    ngOnInit(): void {
        this.fetching$ = this._searchService.feching.asObservable();
        this._paramSub = this._route.queryParams.subscribe((x: Params) => {
            const query = x['query'];
            const page = x['page'] ?? 1;

            if (!query) {
                return;
            }

            this.query = query;

            this.update(query, this.pageToIndex(page)).subscribe((x: IGoogleSearchResult) => {
                this.handleResult(x);
            });
        });
    }

    onSearch(val: string): void {
        this.searchQuery = val;
        this.navigate(1);
    }

    onPageChange(page: number): void {
        this.navigate(page);
    }

    private navigate(page: number) {
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
                query: this.searchQuery,
                page: page,
            },
        });
    }

    private update(query: string, index?: number): Observable<IGoogleSearchResult> {
        return this._searchService.getResults(query, index).pipe(distinctUntilChanged());
    }

    private handleResult(result: IGoogleSearchResult): void {
        this.items = result.items;
        this.queries = result.queries;

        if (!this.request) return;

        const res = +this.request.totalResults;
        const cnt = this.request.count;
        const query = this.request.searchTerms;
        const startIdx = this.request.startIndex;

        this.paging.numberOfPages = this.clamp(res, 0, 100) / cnt;
        this.paging.totalItems = this.clamp(res, 0, 100);
        this.paging.currentPage = this.indexToPage(startIdx, cnt);

        this.searchQuery = query;
        this._viewportScroller.scrollToPosition([0, 0]);

        if (this._paramSub) {
            this._paramSub.unsubscribe();
        }

        this._hasLoaded.next(true);
    }

    private pageToIndex(page: number): number {
        return (page - 1) * 10 + 1;
    }

    private indexToPage(index: number, count: number): number {
        return Math.round(index / count) + 1;
    }

    private clamp(num: number, min: number, max: number): number {
        return Math.max(min, Math.min(num, max));
    }
}
